import styled, { useTheme } from 'styled-components';
import { SignalType } from '../api/types';
import { useState } from 'react';
import o2 from '../../../assets/images/operators/O2.png';
import ee from '../../../assets/images/operators/EE.png';
import three from '../../../assets/images/operators/three.png';
import vodafone from '../../../assets/images/operators/vodafone.png';
import brandConfig from '../../../config/brand';
import ofcomLogo from '../../../assets/images/ofcom-logo.webp';
import { CheckIcon } from '../../../assets/icons/check';
import { CloseIcon } from '../../../assets/icons/close';

type SignalDisplayProps = {
  addresses: SignalType[];
};

type Operators = 'o2' | 'vodafone' | 'ee' | 'farm' | 'three';

export const SignalDisplay = ({ addresses }: SignalDisplayProps) => {
  const [location, setLocation] = useState<'indoor' | 'outdoor'>('outdoor');
  const [address, setAddress] = useState<string | null>(null);

  const selectedAddress = addresses.find((a) => a.AddressShortDescription === address);

  const getSignalValue = (operator: Operators) => {
    if (!selectedAddress) return null;
    switch (operator) {
      case 'vodafone':
        return location === 'indoor'
          ? selectedAddress.VodafoneIndoor
          : selectedAddress.VodafoneOutdoor;
      case 'o2':
        return location === 'indoor' ? selectedAddress.O2Indoor : selectedAddress.O2Outdoor;
      case 'ee':
        return location === 'indoor' ? selectedAddress.EEIndoor : selectedAddress.EEOutdoor;
      case 'farm':
        return location === 'indoor'
          ? selectedAddress.MaxIndoorFarm4
          : selectedAddress.MaxOutdoorFarm4;
      case 'three':
        return location === 'indoor' ? selectedAddress.ThreeIndoor : selectedAddress.ThreeOutdoor;
      default:
        return null;
    }
  };

  const isAddressSelected = address !== null;

  return (
    <Container>
      <PickerWrapper>
        <StyledLabel>Choose an address</StyledLabel>

        <StyledSelect name="address" id="address" onChange={(e) => setAddress(e.target.value)}>
          <option value="">Select an address</option>
          {addresses.map((address) => {
            return (
              <option key={address.AddressShortDescription} value={address.AddressShortDescription}>
                {address.AddressShortDescription}
              </option>
            );
          })}
        </StyledSelect>
      </PickerWrapper>

      <PickerWrapper>
        <StyledLabel>Camera will be located:</StyledLabel>
        <Wrapper>
          <ImageWrapper isSelected={location === 'outdoor'} onClick={() => setLocation('outdoor')}>
            <p>Outdoors</p>
          </ImageWrapper>
          <ImageWrapper isSelected={location === 'indoor'} onClick={() => setLocation('indoor')}>
            <p>Indoors</p>
          </ImageWrapper>
        </Wrapper>
      </PickerWrapper>
      {isAddressSelected && (
        <ResultComponent
          getSignalValue={getSignalValue}
          isSignalBoosterRequired={
            Boolean(selectedAddress?.SignalBoosterRequiredForInside) && location === 'indoor'
          }
        />
      )}
    </Container>
  );
};

type ResultComponentProps = {
  getSignalValue: (operator: Operators) => number | null;
  isSignalBoosterRequired: boolean;
};

const ResultComponent = ({ getSignalValue, isSignalBoosterRequired }: ResultComponentProps) => {
  const { colors } = useTheme();

  const vodafoneValue = getSignalValue('vodafone');
  const o2Value = getSignalValue('o2');
  const eeValue = getSignalValue('ee');
  const farmValue = getSignalValue('farm');
  const threeValue = getSignalValue('three');

  const valuesObject: Record<
    number,
    { text: string; description: string; color: string; isCovered: boolean }
  > = {
    0: {
      text: 'Poor Signal',
      description:
        'Based on Ofcom results, it is unlikely the camera will work at this location. We recommend testing the 4G signal with a mobile phone at the exact location you wish to install your camera. Note: This is only an estimate based on Ofcom data.',
      color: colors.red,
      isCovered: false
    },
    3: {
      text: 'Limited Signal',
      description:
        '4G coverage may be limited in this area. We recommend testing the signal strength with a mobile phone at your planned camera location. Note: This is only an estimate based on Ofcom data.',
      color: colors.orange,
      isCovered: true
    },
    4: {
      text: 'Strong Signal',
      description:
        'You are likely to have good 4G coverage in this area. Your 4G camera should work reliably. Note: This is only an estimate based on Ofcom data. The best test is to check 4G signal using a mobile phone at the exact location you wish to install your camera.',
      color: colors.green900,
      isCovered: true
    }
  };

  const vodafoneDetails = valuesObject[vodafoneValue || 0];
  const o2Details = valuesObject[o2Value || 0];
  const eeDetails = valuesObject[eeValue || 0];
  const farmDetails = valuesObject[farmValue || 0];
  const threeDetails = valuesObject[threeValue || 0];

  return (
    <BottomWrapper>
      <PartnershipHeader>
        <PartnershipText>Network coverage data provided in partnership with</PartnershipText>
        <OfcomLogo src={ofcomLogo} alt="Ofcom" />
      </PartnershipHeader>
      <OperatorsWrapper>
        <LogoOperatorWrapper>
          <Logo src={vodafone} alt="vodafone" />
          <LogoText>Vodafone</LogoText>
          {vodafoneDetails.isCovered ? <Check color={vodafoneDetails.color} /> : <Cross />}
        </LogoOperatorWrapper>
        <LogoOperatorWrapper>
          <Logo src={o2} alt="o2" />
          <LogoText>O2</LogoText>
          {o2Details.isCovered ? <Check color={o2Details.color} /> : <Cross />}
        </LogoOperatorWrapper>
        <LogoOperatorWrapper>
          <Logo src={ee} alt="ee" />
          <LogoText>EE</LogoText>
          {eeDetails.isCovered ? <Check color={eeDetails.color} /> : <Cross />}
        </LogoOperatorWrapper>
        <LogoOperatorWrapper>
          <Logo src={three} alt="three" />
          <LogoText>Three</LogoText>
          {threeDetails.isCovered ? <Check color={threeDetails.color} /> : <Cross />}
        </LogoOperatorWrapper>
      </OperatorsWrapper>
      {isSignalBoosterRequired && (
        <StyledText>
          Your camera may struggle for signal inside; we recommend having a look at our 4G extender
          antenna, which allows you to pick up a stronger signal from outside the building.{' '}
          <StyledLink
            href="https://www.farmstream.co.uk/products/4g-extender-antenna"
            target="_blank"
            rel="noopener noreferrer">
            More info
          </StyledLink>
        </StyledText>
      )}
      <BottomContainer>
        <LogoWrapper>
          <FarmStreamLogo src={brandConfig.logo} alt={brandConfig.displayName} />
          <div>
            <h4>Farmstream multi network SIM</h4>
          </div>
          {farmDetails.isCovered ? <Check color={farmDetails.color} /> : <Cross />}
        </LogoWrapper>
      </BottomContainer>
      <ResultWrapper>
        <ResultContainer color={farmDetails.color}>
          <h3>{farmDetails.text}</h3>
          <p>{farmDetails.description}</p>
        </ResultContainer>
      </ResultWrapper>

      <ButtonWrapper>
        <ReturnButton
          href="https://www.farmstream.co.uk"
          target="_blank"
          rel="noopener noreferrer">
          Return to Farmstream.co.uk
        </ReturnButton>
      </ButtonWrapper>
    </BottomWrapper>
  );
};

const Check = ({ color }: { color: string }) => {
  return (
    <IconWrapper color={color}>
      <CheckIcon size={20} color={color} />
    </IconWrapper>
  );
};

const Cross = () => {
  const { colors } = useTheme();
  const { red } = colors;

  return (
    <IconWrapper color={red}>
      <CloseIcon size={20} color={red} />
    </IconWrapper>
  );
};

const StyledText = styled.p`
  font-size: 1rem;
  text-align: center;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.red};
  margin-top: 1rem;
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.primary400};
  text-decoration: underline;
  font-weight: bold;

  &:hover {
    opacity: 0.8;
  }
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 2rem;
  height: 2rem;
  border: 2px solid ${({ color }) => color};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LogoText = styled.p`
  font-size: 0.8rem;
  font-weight: bold;
`;

const LogoOperatorWrapper = styled.div`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.black};
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const OperatorsWrapper = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.black};
  border-radius: 0.5rem;

  & h4 {
    margin: 0;
  }
`;

const FarmStreamLogo = styled.img`
  width: 4rem;
`;

const OfcomLogo = styled.img`
  height: 3rem;
  width: auto;
`;

const PartnershipText = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.gray900};
  font-style: italic;
`;

const BottomWrapper = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

const ResultContainer = styled.div<{ color: string }>`
  display: flex;
  padding: 1rem;
  background-color: ${({ color }) => color};
  border-radius: 0.5rem;
  flex-direction: column;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  & h3 {
    margin: 0;
    text-align: center;
  }

  & p {
    font-size: 0.9rem;
  }
`;

const ResultWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const ReturnButton = styled.a`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: bold;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary300};
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  padding: 0.5rem;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const ImageWrapper = styled.div<{ isSelected?: boolean }>`
  width: 10rem;
  border: 1px solid ${({ isSelected }) => (isSelected ? 'black' : 'transparent')};
  background-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.gray : 'transparent')};
  cursor: pointer;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.4s;
  flex-grow: 1;
`;

const Logo = styled.img`
  height: 3.5rem;
`;

const StyledSelect = styled.select`
  max-width: 40rem;
  font-family: inherit;
  font-size: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.black};
  border-radius: 0.5rem;
  padding: 0.5rem;
  width: 100%;
`;

const PickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 1rem;
  font-weight: bold;
`;

const PartnershipHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
